import { useEffect, useRef, useState } from "react";
import { Lock, FileText, Loader2, Sparkles, Square, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { startRapportDoubleJob, fetchRapportDoubleJob, fetchRapportDouble, fetchRapportDoubleHistorique, fetchRapportDoubleById, downloadRapportDoublePdf } from "@/lib/ia";
import RapportDoubleHistorique from "@/components/ia/RapportDoubleHistorique";
import ReportShell from "@/components/reports/ReportShell";
import { cn } from "@/lib/utils";

const TAG_TONE = {
    Sauvegarde: "bg-emerald-100 text-emerald-800 border-emerald-300", Go: "bg-emerald-100 text-emerald-800 border-emerald-300",
    "Redressement Judiciaire": "bg-amber-100 text-amber-800 border-amber-300",
    Liquidation: "bg-rose-100 text-rose-800 border-rose-300", "No Go": "bg-rose-100 text-rose-800 border-rose-300",
};
const eur = (v) => (v == null || v === "" || Number.isNaN(Number(v)) ? "n/c" : `${Math.round(Number(v)).toLocaleString("fr-FR")} €`);

export default function RapportDoubleSection({ dossierId }) {
    const [note, setNote] = useState("");
    const [rapport, setRapport] = useState("");
    const [meta, setMeta] = useState(null);
    const [tab, setTab] = useState("rapport");
    const [streaming, setStreaming] = useState(false);
    const [stage, setStage] = useState("");
    const [createdAt, setCreatedAt] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [historique, setHistorique] = useState([]);
    const [exporting, setExporting] = useState(false);
    const [chartsNote, setChartsNote] = useState([]);
    const [chartsRapport, setChartsRapport] = useState([]);
    const abortRef = useRef(null);

    function apply(r) {
        setNote(r.note); setRapport(r.rapport); setMeta(r.meta); setCreatedAt(r.created_at); setSessionId(r.session_id);
        setChartsNote(r.charts_note || []); setChartsRapport(r.charts_rapport || []);
    }
    const loadHistorique = () => fetchRapportDoubleHistorique(dossierId).then(setHistorique).catch(() => {});

    useEffect(() => {
        fetchRapportDouble(dossierId).then((r) => { if (r) apply(r); }).catch(() => {});
        loadHistorique();
    }, [dossierId]); // eslint-disable-line react-hooks/exhaustive-deps

    async function select(id) {
        try { apply(await fetchRapportDoubleById(dossierId, id)); } catch (e) { toast.error(e.message); }
    }

    async function exportPdf(kind) {
        if (!sessionId) return;
        setExporting(true);
        try {
            const name = await downloadRapportDoublePdf(dossierId, sessionId, kind);
            toast.success("PDF exporté", { description: name });
        } catch (e) {
            toast.error("Export PDF", { description: e.message });
        } finally {
            setExporting(false);
        }
    }

    async function run() {
        setStreaming(true); setNote(""); setRapport(""); setMeta(null); setSessionId(null); setStage("preparation"); setTab("note");
        const ctl = { cancelled: false };
        abortRef.current = { abort: () => { ctl.cancelled = true; } };
        try {
            const { job_id } = await startRapportDoubleJob(dossierId);
            while (!ctl.cancelled) {
                await new Promise((r) => setTimeout(r, 3000));
                const j = await fetchRapportDoubleJob(dossierId, job_id);
                setNote(j.note || ""); setRapport(j.rapport || "");
                if (j.stage === "rapport" && stage !== "rapport") setTab("rapport");
                setStage(j.stage);
                if (j.stage === "done") {
                    setMeta(j.meta || {}); setCreatedAt(new Date().toISOString()); setSessionId(j.session_id); loadHistorique();
                    toast.success("Note confidentielle et rapport officiel générés");
                    break;
                }
                if (j.stage === "error") {
                    toast.error("Erreur IA", { description: String(j.error), duration: 12000 });
                    break;
                }
            }
            if (ctl.cancelled) toast.info("Suivi interrompu — la génération se termine en arrière-plan et apparaîtra dans l'historique.");
        } catch (e) {
            toast.error("Génération impossible", { description: e.message, duration: 12000 });
        } finally {
            setStreaming(false); setStage(""); abortRef.current = null;
        }
    }

    const tags = Array.isArray(meta?.tags) ? meta.tags : [];

    return (
        <section data-testid="rapport-double-section" className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h2 className="font-display text-2xl text-foreground">Double workflow : Note confidentielle → Rapport officiel</h2>
                    <p className="text-xs text-muted-foreground">Chaînage IA : la note crue (avocat) est produite d&apos;abord, puis le rapport lissé (tribunal) en omettant les éléments toxiques. Contexte : balance agrégée, top charges, preuves, détection ciseaux / intra-groupe.</p>
                </div>
                <div className="flex items-center gap-2">
                    {streaming && <span data-testid="rapport-double-stage" className="text-xs text-muted-foreground">{stage === "note" ? "Rédaction de la note confidentielle…" : stage === "rapport" ? "Rédaction du rapport officiel…" : "Lecture du dossier…"}</span>}
                    {streaming ? (
                        <Button type="button" variant="outline" data-testid="rapport-double-cancel" onClick={() => abortRef.current?.abort()} className="rounded-full"><Square className="h-4 w-4" /> Annuler</Button>
                    ) : (
                        <Button type="button" data-testid="rapport-double-generate" onClick={run} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"><Sparkles className="h-4 w-4" /> {rapport ? "Regénérer" : "Générer Note + Rapport"}</Button>
                    )}
                </div>
            </div>

            {(rapport || meta || streaming) && (
                <div data-testid="rapport-header" className="rounded-md border border-border bg-muted/50 px-5 py-4 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Tags de décision</span>
                        {tags.length ? tags.map((t) => (
                            <span key={t} data-testid={`decision-tag-${t.replace(/\s+/g, "-").toLowerCase()}`} className={cn("rounded-full border px-3 py-1 text-sm font-bold", TAG_TONE[t] || "bg-muted text-foreground border-border")}>[{t}]</span>
                        )) : <span className="text-xs text-muted-foreground">{streaming ? "en cours…" : "non déterminés"}</span>}
                        {createdAt && <span className="ml-auto text-xs text-muted-foreground">{new Date(createdAt).toLocaleString("fr-FR")}</span>}
                    </div>
                    <div data-testid="encart-synthese" className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {[
                            { k: "cash_burn_mensuel", label: "Cash burn mensuel", v: eur(meta?.cash_burn_mensuel), t: "cash-burn" },
                            { k: "dcp", label: "Date de cessation des paiements estimée", v: meta?.date_cessation_paiement_estimee || "n/c", t: "dcp" },
                            { k: "passif", label: "Passif exigible", v: eur(meta?.passif_exigible), t: "passif-exigible" },
                        ].map((m) => (
                            <div key={m.k} data-testid={`metric-${m.t}`} className="rounded-xl border border-slate-200 bg-white shadow-sm px-4 py-3">
                                <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">{m.label}</p>
                                <p className="mt-1 font-display text-2xl text-foreground tabular-nums">{m.v}</p>
                            </div>
                        ))}
                    </div>
                    {meta?.commentaire && <p className="text-sm text-muted-foreground italic">{meta.commentaire}</p>}
                </div>
            )}

            {(note || rapport || streaming) && (
                <>
                    <nav data-testid="rapport-double-tabs" className="flex gap-1 border-b-2 border-slate-200">
                        <button type="button" data-testid="tab-note-confidentielle" aria-selected={tab === "note"} onClick={() => setTab("note")}
                            className={cn("flex items-center gap-2 px-4 py-2.5 text-sm font-medium -mb-0.5 border-b-[3px] rounded-t-md transition-colors", tab === "note" ? "border-rose-600 bg-rose-50/70 text-rose-700 font-semibold" : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100/70")}>
                            <Lock className="h-4 w-4 text-rose-600" strokeWidth={2} /> Note confidentielle (avocat)
                        </button>
                        <button type="button" data-testid="tab-rapport-officiel" aria-selected={tab === "rapport"} onClick={() => setTab("rapport")}
                            className={cn("flex items-center gap-2 px-4 py-2.5 text-sm font-medium -mb-0.5 border-b-[3px] rounded-t-md transition-colors", tab === "rapport" ? "border-indigo-600 bg-indigo-50/70 text-indigo-700 font-semibold" : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100/70")}>
                            <FileText className="h-4 w-4" strokeWidth={1.75} /> Rapport officiel (tribunal)
                        </button>
                    </nav>
                    <div data-testid={tab === "note" ? "panel-note-confidentielle" : "panel-rapport-officiel"} className={cn("rounded-md border p-5", tab === "note" ? "border-rose-200 bg-rose-50/30" : "border-border bg-card")}>
                        <div className="mb-3 flex items-center justify-between gap-3">
                            {tab === "note" ? <p className="text-xs font-semibold uppercase tracking-wider text-rose-700 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Strictement confidentiel — ne pas diffuser</p> : <p className="text-xs uppercase tracking-wider text-muted-foreground">Rapport institutionnel — tribunal / conseils</p>}
                            {sessionId && !streaming && (
                                <Button type="button" variant="outline" size="sm" data-testid={tab === "note" ? "pdf-note-button" : "pdf-rapport-button"} onClick={() => exportPdf(tab)} disabled={exporting}
                                    className={cn("rounded-full", tab === "note" ? "border-rose-300 text-rose-700 hover:bg-rose-50" : "border-primary/30 text-primary")}>
                                    {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Télécharger PDF
                                </Button>
                            )}
                        </div>
                        {(tab === "note" ? note : rapport) ? (
                            <ReportShell key={`${sessionId}-${tab}`} dossierId={dossierId} sessionId={sessionId} kind={tab === "note" ? "note_confidentielle" : "rapport_officiel"} content={tab === "note" ? note : rapport} onContentChange={tab === "note" ? setNote : setRapport} charts={tab === "note" ? chartsNote : chartsRapport} onChartsChange={tab === "note" ? setChartsNote : setChartsRapport} streaming={streaming} />
                        ) : (
                            <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> En attente…</p>
                        )}
                    </div>
                </>
            )}
            <RapportDoubleHistorique items={historique} selectedId={sessionId} onSelect={select} />
        </section>
    );
}
